import { ROUTER_PATHS, gridSizeMdLg6 } from "@/shared";
import {
    CustomNumberTextField,
    CustomTextField, SampleCheckbox, SampleDatePicker, SampleTable, SingleFormBoxScene,
    SingleTableBoxScene, TabTexLabelCustomSpace, TabTextLabel
} from "@/shared/components/ui";
import { Button, Divider, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowBack, Add, RestoreFromTrash, Edit } from '@mui/icons-material';
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup';
import { ActividadesFibra } from "@/shared/interfaces/erp/noc/orden-trabajo-noc.interface";
import { useNavigate } from "react-router-dom";
import { MRT_ColumnDef } from "material-react-table";
import ViewActividadesModal from "../../components/ViewActividadesFibraModal";
import { toast } from "react-toastify";
import { useCreateWorkOrder } from "../../actions/noc-module.actions";
import CustomAutocompleteFetchForm from "@/shared/components/ui/CustomAutocompleteFetchForm";
import useAutocompleteFetch from "@/shared/hooks/useAutocompleteFetch";
import { Flota } from "@/shared/interfaces";
import { useMemo, useState } from "react";
import EditActividadesModal from "../../components/EditActividadesFibraModal";

export default function CrearOrdenTrabajoNoc() {

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const returnUrlOrdenTrabajoPage = ROUTER_PATHS.noc.ordenesDeTrabajoNocListaNav;
    const username = JSON.parse(localStorage.getItem("auth") || "{}")?.state?.user?.username;

    const [openActividadesModal, setOpenActividadesModal] = useState(false);

    // hook para obtener flotas
    const { fetchAutocompleteFlota } = useAutocompleteFetch();

    // hook para registrar orden de trabajo noc
    const useCreateWorkOrderMutation = useCreateWorkOrder();

    const newOrdenTrabajoSchema = yup.object({
        fleetCode: yup.string().required('La flota es requerida'),
        workDate: yup.date().transform((value, originalValue) =>
            originalValue === "" ? null : value
        ).required('La fecha de trabajo es requerida'),
        orderName: yup.string().required('El nombre es requerido'),
    });

    // PERF: SaveFormData = Omit<OrdenTrabajoNoc> (con sub-objetos anidados) hacía explotar Path<T>.
    // Interfaz FLACA con solo los campos usados (string) → errors.X sigue siendo FieldError. Runtime igual.
    const form = useForm<{ orderName: string; workDate: string; previousWorkDate: string; mobilizationValue: number; fleetCode: string; isScope: boolean; activitiesOrder: any[]; userRegister?: string; fleetName?: string }>({
        resolver: yupResolver(newOrdenTrabajoSchema as any),
        defaultValues: {
            fleetCode: '',
            fleetName: '',
            workDate: '',
            orderName: '',
            mobilizationValue: 0,
            userRegister: username,
            isScope: false,
            previousWorkDate: '',
            activitiesOrder: [] as ActividadesFibra[],
        },
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isValid },
    } = form;

    const onSave = () => {
        if (!isValid) return;

        if (form.getValues().activitiesOrder.length === 0) {
            toast.error("Debe agregar al menos una actividad");
            return false;
        }

        useCreateWorkOrderMutation.mutate(form.getValues() as any, {
            onSuccess: () => {
                // Invalidar la query de la lista de órdenes para refrescar la tabla
                queryClient.invalidateQueries({ queryKey: ['workOrder'] }); // Ajusta la key según tu hook
                navigate(returnUrlOrdenTrabajoPage);
                toast.success("Orden registrada correctamente")
            },
            onError: (error) => {
                console.error('Error al crear la orden de trabajo:', error);
                toast.error('Error al crear la orden de trabajo');
            }
        });
    };


    const [editActividadesModal, setEditActividadesModal] = useState(false);
    const [selectActivity, setSelectActivity] = useState([]);
    const onEditActivity = (data: any) => {
        setEditActividadesModal(true);
        setSelectActivity(data)
    }

    //
    const dataMapped = useWatch({ control: form.control, name: 'activitiesOrder' }) || [];
    const columns = useMemo<MRT_ColumnDef<ActividadesFibra>[]>(
        () => [
            {
                header: "Acciones",
                Cell: ({ row }) => (
                    <>
                        <Button color={'info'} onClick={() => {
                            onEditActivity(row.original)
                        }}>
                            <Edit />
                        </Button>
                        <Button color={'error'} onClick={() => {
                            const currentActivities = form.getValues().activitiesOrder;
                            const newActivities = currentActivities.filter((_, i) => i !== row.index);
                            form.setValue('activitiesOrder', newActivities);
                        }}>
                            <RestoreFromTrash />
                        </Button>
                    </>
                ),
                size: 50
            },
            {
                header: "Dirigido A",
                accessorKey: "forTheActivity",
                size: 200,
            },
            {
                header: "Nombre Nodo / Cliente",
                size: 150,
                Cell: ({ row }) => (
                    <>
                        {row.original.forTheActivity == 'CLIENTE' ? row.original.clientName : row.original.nodeName}
                    </>
                ),
            },
            {
                header: "Tipo",
                accessorKey: "type",
                size: 200,
            },
            {
                header: "Nombre Actividad",
                accessorKey: "activityName",
                size: 200,
            },
            {
                header: "Tipo Actividad",
                accessorKey: "nameActivityType",
                size: 150,
            },
            {
                header: "Incidencia",
                accessorKey: "nameIncidence",
                size: 100,
            },
            {
                header: "Sector",
                accessorKey: "sector",
                size: 150,
            },
            {
                header: "Tipo de Servicio",
                accessorKey: "service",
                size: 100,
            },
            {
                header: "Observaciones",
                accessorKey: "activityDetail",
                size: 300,
            },
            {
                header: "REQUIERE DE CAJAS",
                accessorKey: "isBoxInstallation",
                size: 50,
                Cell: ({ row }) => row.original?.isBoxInstallation ? "SI" : "NO"
            },
            {
                header: "DETALLE DE CAJAS",
                accessorKey: "primarySecondaryBox",
                size: 200,
                Cell: ({ row }) => {
                    let cajas = '';
                    row.original?.primarySecondaryBox.map((item) => {
                        cajas += item.name + ', ';
                    })
                    return (cajas)
                }
            },
        ],
        []
    );

    const fechaIncial = new Date();
    fechaIncial.setDate(fechaIncial.getDate() - 3);

    const fechaMaxima = new Date();
    fechaMaxima.setDate(fechaMaxima.getDate() + 7);

    // spinner hasta cargas las flotas
    // if (isLoadingFlotas) {
    //     return <LoaderComponent />
    // }

    return (
        <>
            <SingleTableBoxScene
                title={"Crear Orden de Trabajo"}
                textRedirect={"Atras"}
                iconRedirect={<ArrowBack />}
                urlRedirect={`${returnUrlOrdenTrabajoPage}`}
                visibleReport={true}
            >

                <SingleFormBoxScene
                    titlePage={""}
                    onCancel={() => {
                        navigate(returnUrlOrdenTrabajoPage);
                    }}
                    containerMaxWidth={"xl"}
                    py={6}
                    onSave={handleSubmit(onSave)}
                >

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TabTextLabel
                                text={"INFORMACIÓN GENERAL"}
                                mt={0}
                                pb={2}
                                pl={0}
                                pt={2}
                            />
                        </Grid>

                        <CustomAutocompleteFetchForm<Flota>
                            required={true}
                            control={control}
                            size={gridSizeMdLg6}
                            name="fleetCode"
                            label="Buscar Flota"
                            fetchData={fetchAutocompleteFlota}
                            //isAll={true}
                            getOptionLabel={(opt) => opt.nombre || ''}
                            key={'autocomplete-codigo'}
                            getOptionKey={(o) => o.codigo!}
                            keySearch="nombre"
                            loadOptionByValue={async (value) => {
                                const { options } = await fetchAutocompleteFlota(
                                    value as string,
                                    1,
                                    false,
                                    'codigo',
                                    { page_size: 1 }
                                );
                                return options[0] ?? null;
                            }}
                            onChangeRawValue={(flota) => {
                                reset({
                                    ...form.getValues(),
                                    fleetCode: flota?.codigo || '',
                                    fleetName: flota?.nombre || ''
                                });
                            }}
                        />

                        <SampleDatePicker
                            label="Fecha de Trabajo"
                            name="workDate"
                            control={form.control}
                            defaultValue={form.getValues().workDate || ''}
                            error={errors.workDate}
                            helperText={errors.workDate?.message ?? ''}
                            size={gridSizeMdLg6}
                            minDate={fechaIncial}
                            maxDate={fechaMaxima}
                        />

                        <CustomTextField
                            label="Nombre de la Orden"
                            name="orderName"
                            control={form.control}
                            defaultValue={form.getValues().orderName || ''}
                            error={errors.orderName}
                            helperText={errors.orderName?.message ?? ''}
                            size={gridSizeMdLg6}
                        />

                        <CustomNumberTextField
                            label="Valor Movilización opcional"
                            name="mobilizationValue"
                            control={form.control}
                            defaultValue={form.getValues().mobilizationValue || ''}
                            error={errors.mobilizationValue}
                            helperText={errors.mobilizationValue?.message ?? ''}
                            size={gridSizeMdLg6}
                            required={false}
                        />

                        <Grid item {...gridSizeMdLg6}>
                            <SampleCheckbox
                                label="Es Orden de Alcance ?"
                                name="isScope"
                                control={control}
                                defaultValue={!!form.getValues().isScope || false}
                                textNote={"Si la orden es un complemento a una anterior."}
                                isState={false}
                            />
                        </Grid>

                        <SampleDatePicker
                            label="Fecha de Trabajo Anterior"
                            name="previousWorkDate"
                            control={form.control}
                            defaultValue={form.getValues().previousWorkDate || ''}
                            error={errors.previousWorkDate}
                            helperText={errors.previousWorkDate?.message ?? ''}
                            size={gridSizeMdLg6}
                            maxDate={fechaMaxima}
                        />

                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Divider sx={{ my: 4 }} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TabTexLabelCustomSpace
                                textContent={"LISTADO DE ACTIVIDADES"}
                                colorBtn={"success"}
                                startIconBtn={<Add />}
                                labelBtn={"Agregar Actividad"}
                                onClickBtn={() => { setOpenActividadesModal(true); }}
                            />
                        </Grid>
                        <SampleTable
                            columns={columns}
                            data={dataMapped || []}
                        />
                    </Grid>

                </SingleFormBoxScene>

            </SingleTableBoxScene>


            {/* ----------- Modals ----------- */}
            {openActividadesModal && (
                <ViewActividadesModal
                    open={openActividadesModal}
                    setOpen={setOpenActividadesModal}
                    form={form as any}
                />
            )}


            {editActividadesModal && (
                <EditActividadesModal
                    open={editActividadesModal}
                    setOpen={setEditActividadesModal}
                    form={form as any}
                    selectActivity={selectActivity}
                />
            )}

        </>
    );
}